/**
 * Route guard — refuses a page the signed-in user's roles do not allow.
 *
 * Wraps <Routes> once rather than decorating each of the ~47 routes. The rule
 * comes from `rolesForPath`, the same table that drives the side nav, the app
 * switcher and the command palette, because four independent copies of "who may
 * see this" is how a page ends up hidden from the nav yet reachable by typing
 * the URL.
 *
 * This is UX, not security. The backend enforces authorization; every gated
 * path here mirrors a real @PreAuthorize (see docs/role-matrix.md in the
 * backend repo). The value is that a user is told plainly instead of walking
 * into a page that renders and then 403s on every request.
 */

import { useLocation } from 'react-router-dom';
import { useCurrentUser } from '../lib/currentUser';
import { rolesForPath } from '../lib/apps';
import { allowedByRoles, roleLabel } from '../lib/roles';
import TopBar from './TopBar';

/** Shown in place of the page when the user's roles do not cover it. */
export function NoAccess({ required = [] }) {
  const { role } = useCurrentUser();
  return (
    <>
      <TopBar crumbs={['Home', 'No access']} />
      <div className="app-content">
        <div
          className="card"
          style={{ maxWidth: 520, margin: '10vh auto', textAlign: 'center', padding: '2rem' }}
        >
          <div style={{ fontSize: 34, lineHeight: 1, marginBottom: 12 }} aria-hidden="true">🔒</div>
          <h1 style={{ fontSize: '1.125rem', fontWeight: 600, marginBottom: 8 }}>
            You don’t have access to this page
          </h1>
          <p className="text-sm text-muted" style={{ marginBottom: 16 }}>
            {/* Name the role that would work rather than only refusing — it turns
                "it's broken" into a request the user can actually make. */}
            You’re signed in as <strong>{roleLabel(role)}</strong>.
            {required.length > 0 && (
              <> This page is for {required.map(roleLabel).join(', ')}.</>
            )}
          </p>
          <p className="mono text-xs text-muted">
            Ask a Dealer Principal if you need access.
          </p>
        </div>
      </div>
    </>
  );
}

export default function RequireAccess({ children }) {
  const { pathname } = useLocation();
  const { roles, loading, user } = useCurrentUser();
  const required = rolesForPath(pathname);

  // Unrestricted path — the common case, and the default for anything the
  // backend does not itself restrict.
  if (!required) return children;

  /*
   * Roles arrive in two waves: synchronously from the Keycloak token, then
   * authoritatively from GET /v1/users/me. Rendering "no access" during that
   * gap would flash a denial at legitimate users on every page load, so hold
   * until we know who they are.
   */
  if (loading && !user) {
    return (
      <>
        <TopBar crumbs={['Home']} />
        <div className="app-content">
          <div className="card" style={{ minHeight: 220, opacity: 0.4 }} />
        </div>
      </>
    );
  }

  if (!allowedByRoles(roles, required)) return <NoAccess required={required} />;
  return children;
}
